import { useState, useEffect, useRef } from 'react'
import API from '../services/api.js'

const LIGHT_POLL_INTERVAL_MS = 10000
const RCM_DETAIL_POLL_INTERVAL_MS = 30000

export async function fetchAssets() {
  const { data } = await API.get('/controls/assets/')
  return data || []
}

export function useRCMs(assetId) {
  const [rcms, setRcms] = useState([])
  const [autoRCM, setAutoRCM] = useState(null)
  const [pendingQueueCount, setPendingQueueCount] = useState(0)
  const [failedQueueCount, setFailedQueueCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const hasLoadedOnce = useRef(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState(null)
  const [syncError, setSyncError] = useState(null)
  const pollRef = useRef(null)

  useEffect(() => {
    if (!assetId) {
      setLoading(false)
      return
    }

    fetchRCMs().then(() => ensureSeeded(assetId))
    fetchQueueCount()
    syncRCM()   // ADDED — drains any pending RCMSyncQueue items on load, not just on manual "Sync Now" click

    pollRef.current = setInterval(() => {
      fetchQueueCount()
      fetchRCMs()
    }, LIGHT_POLL_INTERVAL_MS)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId])

  async function fetchRCMs() {
    if (!assetId) return
    if (!hasLoadedOnce.current) setLoading(true)
    try {
      const { data } = await API.get('/rcm/', { params: { asset_id: assetId } })
      setAutoRCM(data?.find(r => r.source === 'auto') ?? null)
      setRcms(data?.filter(r => r.source !== 'auto') ?? [])
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
      hasLoadedOnce.current = true
    }
  }

  async function ensureSeeded(assetIdParam) {
    if (!assetIdParam) return
    try {
      const { data } = await API.post('/rcm/seed/', { asset_id: assetIdParam })
      console.log(`ensureSeeded: seeded ${data?.seeded ?? 0} gap rows total`)
      await fetchRCMs()
    } catch (err) {
      console.warn('ensureSeeded failed:', err?.response?.data?.detail || err.message)
    }
  }

  async function fetchQueueCount() {
    if (!assetId) return
    try {
      const { data } = await API.get('/rcm/queue-status/', { params: { asset_id: assetId } })
      setPendingQueueCount(data?.pending_count ?? 0)
      setFailedQueueCount(data?.failed_count ?? 0)
    } catch {
      setPendingQueueCount(0)
      setFailedQueueCount(0)
    }
  }

  async function syncRCM() {
    if (syncing || !assetId) return
    setSyncing(true)
    setSyncError(null)
    try {
      await API.post('/rcm/sync/', { asset_id: assetId })
      await Promise.all([fetchRCMs(), fetchQueueCount()])
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message
      console.error('Sync failed:', msg)
      setSyncError(msg)
    } finally {
      setSyncing(false)
    }
  }

  async function rebuildRCM() {
    if (syncing || !assetId) return
    setSyncing(true)
    setSyncError(null)
    try {
      await API.post('/rcm/backfill/', { asset_id: assetId })
      await API.post('/rcm/seed/', { asset_id: assetId })
      await API.post('/rcm/sync/', { asset_id: assetId })
      await Promise.all([fetchRCMs(), fetchQueueCount()])
    } catch (err) {
      const message = err?.response?.data?.detail || err?.response?.data?.error || err.message
      setSyncError(message)
      throw err
    } finally {
      setSyncing(false)
    }
  }

  async function createRCM(assetIdParam, name, description, version) {
    try {
      const { data } = await API.post('/rcm/', {
        asset_id: assetIdParam,
        name,
        description: description || null,
        version: version || '1.0',
      })
      await fetchRCMs()
      return data
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function updateRCM(id, updates) {
    try {
      await API.patch(`/rcm/${id}/`, { ...updates, asset_id: assetId })
      await fetchRCMs()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  return {
    rcms,
    autoRCM,
    pendingQueueCount,
    failedQueueCount,
    loading,
    syncing,
    error,
    syncError,
    createRCM,
    updateRCM,
    syncRCM,
    rebuildRCM,
    ensureSeeded,
    refetch: fetchRCMs,
  }
}

export function useRCMDetail(id, assetId) {
  const [rcm, setRcm] = useState(null)
  const [rcmControls, setRcmControls] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const pollRef = useRef(null)

  useEffect(() => {
    async function loadUser() {
      try {
        const { data } = await API.get('/auth/me/')
        setCurrentUser(data)
      } catch (err) {
        console.error('Failed to fetch current user:', err?.response?.data?.detail || err.message)
      }
    }
    loadUser()
  }, [])

  useEffect(() => {
    if (!id || !assetId) return
    fetchRCM()
    fetchRCMControls()

    pollRef.current = setInterval(() => {
      fetchRCM()
      fetchRCMControls()
    }, RCM_DETAIL_POLL_INTERVAL_MS)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assetId])

  async function fetchRCM() {
    try {
      const { data } = await API.get(`/rcm/${id}/`, { params: { asset_id: assetId } })
      setRcm(data)
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  async function fetchRCMControls() {
    try {
      const [controlsRes, overridesRes] = await Promise.all([
        API.get(`/rcm/${id}/controls/`, { params: { asset_id: assetId } }),
        API.get(`/rcm/${id}/overrides/`, { params: { asset_id: assetId } }).catch(err => {
          console.warn('fetchOverrides failed:', err?.response?.data?.detail || err.message)
          return { data: [] }
        }),
      ])

      const data = controlsRes.data
      if (!data) return
      const overridesData = overridesRes.data || []

      const overridesMap = {}
      overridesData.forEach(o => {
        overridesMap[o.trusted_rcm_control_id] = o
      })

      setRcmControls(
        data.map(rc => {
          const displayControls = rc.controls ?? (rc.trusted_rcm_control_id
            ? {
                id: null,
                control_id: rc.trusted_rcm_control_id,
                title: rc.trusted_rcm_control_id,
                description: null,
                category: null,
                frameworks: null,
              }
            : null)

          const enrichment = (() => {
            try { return rc.notes ? JSON.parse(rc.notes) : null }
            catch { return null }
          })()

          const override = overridesMap[rc.trusted_rcm_control_id] ?? null

          const resolved = {
            implementation: override?.implementation_override ?? enrichment?.control_implementation ?? null,
            process_owner: override?.process_owner_override ?? enrichment?.probable_process_owner ?? null,
            testing_notes: override?.testing_notes_override ?? enrichment?.testing_attributes ?? null,
            review_comments: override?.review_comments ?? null,
            further_comments: override?.further_comments ?? null,
            last_reviewed_by: override?.reviewer_name ?? null,
            last_reviewed_at: override?.last_reviewed_at ?? null,
          }

          return {
            ...rc,
            controls: displayControls,
            coverage_status: rc.coverage_status || enrichment?.coverage_status || 'gap',
            evidence_count: rc.evidence_count || 0,
            last_reviewed: rc.last_reviewed || null,
            enrichment,
            override,
            resolved,
          }
        })
      )
    } catch (err) {
      console.error('fetchRCMControls failed:', err?.response?.data?.detail || err.message)
    }
  }

  async function saveOverride(trustedRcmControlId, fields) {
    const payload = {
      trusted_rcm_control_id: trustedRcmControlId,
      asset_id: assetId,
    }

    if ('implementation_override' in fields) payload.implementation_override = fields.implementation_override
    if ('process_owner_override' in fields) payload.process_owner_override = fields.process_owner_override
    if ('testing_notes_override' in fields) payload.testing_notes_override = fields.testing_notes_override
    if ('review_comments' in fields) payload.review_comments = fields.review_comments
    if ('further_comments' in fields) payload.further_comments = fields.further_comments

    try {
      await API.post(`/rcm/${id}/overrides/`, payload)
      await fetchRCMControls()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function addControl(controlId, notes) {
    try {
      await API.post(`/rcm/${id}/controls/`, {
        control_id: controlId,
        notes: notes || null,
        asset_id: assetId,
      })
      await fetchRCMControls()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function removeControl(rcmControlId) {
    try {
      await API.delete(`/rcm/controls/${rcmControlId}/`, { params: { asset_id: assetId } })
      await fetchRCMControls()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  return {
    rcm,
    rcmControls,
    loading,
    error,
    currentUser,
    addControl,
    removeControl,
    saveOverride,
    refetch: fetchRCMControls,
  }
}
